import { useState, useEffect, useLayoutEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { authAPI } from "@/lib/api";
import { toast } from "sonner";

const BRAND_LOGO = "/logo-black.png";

export default function LoginPageNew() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [totpRequired, setTotpRequired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(false);

  // Das Login gehört zum CRM und trägt dessen Stil samt der auf diesem Gerät gewählten Akzentfarbe (Betreiber 12.10.2026:
  // „passe auch das Login an"). Alle anderen öffentlichen Seiten bekommen die Klasse nicht.
  useLayoutEffect(() => {
    document.documentElement.classList.add("crm-neu");
    return () => document.documentElement.classList.remove("crm-neu");
  }, []);

  useEffect(() => {
    authAPI.getConfig()
      .then(res => setRegistrationEnabled(!!res.data.registration_enabled))
      .catch(() => setRegistrationEnabled(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { email, password };
      if (totpCode) payload.totp_code = totpCode;
      const res = await authAPI.login(payload);
      login(res.data.token, res.data.user);
      toast.success(`Willkommen zurück, ${res.data.user.name}!`);
      navigate(res.data.user.startseite || "/heute");   // Stations-Rollen landen auf ihrer Seite
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.totp_required) {
        setTotpRequired(true);
        if (totpCode) toast.error(detail.message || "Ungültiger 2FA-Code");
        else toast.info("Bitte 2FA-Code eingeben");
      } else {
        toast.error(typeof detail === "string" ? detail : "Anmeldung fehlgeschlagen");
      }
    } finally {
      setLoading(false);
    }
  };

  const feld = "w-full rounded-xl border border-input bg-background px-4 py-3 text-base outline-none transition-all duration-200 placeholder:text-muted-foreground/70 focus:border-primary focus:ring-2 focus:ring-primary/25";

  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground flex flex-col items-center justify-center px-4 py-10" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* zwei weiche Farbflächen in der Akzentfarbe — reine Zierde */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -right-32 h-[28rem] w-[28rem] rounded-full bg-primary/10 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-48 -left-32 h-[26rem] w-[26rem] rounded-full bg-primary/5 blur-3xl" />

      <div data-testid="login-card" className="p1-rein relative w-full max-w-md rounded-3xl border border-border/60 bg-card p-8 sm:p-10 shadow-[var(--schatten)]">
        <div className="flex justify-center mb-6">
          <img src={BRAND_LOGO} alt="Performance Center" data-testid="login-logo" className="h-14 object-contain dark:invert" />
        </div>
        <div className="mb-7 text-center">
          <h1 className="text-2xl font-extrabold tracking-tight">Willkommen zurück</h1>
          <p className="mt-1 text-sm text-muted-foreground">Melde dich an, um ins CRM zu kommen.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="login-email" className="mb-1.5 block text-xs font-semibold text-muted-foreground">E-Mail</label>
            <input
              id="login-email"
              data-testid="login-email-input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@p1-pforzheim.de"
              className={feld}
              required
            />
          </div>
          <div>
            <label htmlFor="login-passwort" className="mb-1.5 block text-xs font-semibold text-muted-foreground">Passwort</label>
            <input
              id="login-passwort"
              data-testid="login-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Passwort"
              className={feld}
              required
            />
          </div>

          {totpRequired && (
            <div className="p1-rein">
              <label htmlFor="login-totp" className="mb-1.5 block text-xs font-semibold text-muted-foreground">Code aus der Authenticator-App</label>
              <input
                id="login-totp"
                data-testid="login-totp-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                placeholder="6 Ziffern"
                className={`${feld} text-center tracking-widest font-mono`}
                autoFocus
                required
              />
            </div>
          )}

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="p1-knopf w-full rounded-full bg-primary py-3 text-sm font-bold tracking-wide text-primary-foreground hover:bg-marke-dunkel focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-card disabled:opacity-50"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Wird angemeldet …
              </span>
            ) : "Anmelden"}
          </button>
        </form>

        {registrationEnabled && (
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Noch kein Konto?{" "}
            <Link to="/registrieren" data-testid="login-register-link" className="text-primary hover:underline font-semibold">
              Hier registrieren
            </Link>
          </p>
        )}
      </div>

      {/* Rechtliches — von jeder öffentlichen Seite erreichbar */}
      <div className="relative mt-6 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid="login-legal-links">
        <Link to="/impressum" className="hover:text-foreground">Impressum</Link>
        <Link to="/datenschutz" className="hover:text-foreground">Datenschutz</Link>
        <Link to="/agb" className="hover:text-foreground">AGB</Link>
        <Link to="/cookies" className="hover:text-foreground">Cookies</Link>
      </div>
    </div>
  );
}
